import { useRouter as useNextRouter } from "next/router";
import { useEffect } from "react";

import {
  accountsUrl,
  coverageUrl,
  coverageWhatYouPayUrl,
  peopleUrl,
  settingsAccountSettingsUrl,
  settingsReleaseListUrl,
  settingsUrl,
} from "@medbillai/utils";

import { rememberSectionEntry } from "./openedInApp";

// Each tab's root and the first path segments it owns.
const TAB_SECTIONS: readonly (readonly [string, readonly string[]])[] = [
  ["/", ["/"]],
  ["/cases", ["/cases", "/case"]],
  ["/bills", ["/bills", "/bill", "/bill-dispute"]],
  ["/accounts", ["/accounts"]],
  [coverageUrl(), [coverageUrl()]],
  ["/settings", ["/settings", "/hipaa-release", "/people"]],
];

export const tabPathMatch = (pathName: string): string => {
  const firstSection = `/${pathName.split("/")[1] || ""}`;
  return (
    TAB_SECTIONS.find(([, sections]) => sections.includes(firstSection))?.[0] ??
    "/"
  );
};

// Nested screens whose parent is not their tab's root.
const NESTED_PARENTS: Record<string, string> = {
  "/coverage/what-you-pay/[category]": coverageWhatYouPayUrl(),
  "/people/create": peopleUrl(),
  "/people/edit": peopleUrl(),
  "/settings/delete-account": settingsAccountSettingsUrl(),
  "/hipaa-release-preview": settingsUrl(),
  "/hipaa-release/error": settingsReleaseListUrl(),
  "/link-insurance": accountsUrl(),
};

export const parentRoute = (pathName: string): string =>
  NESTED_PARENTS[pathName] ?? tabPathMatch(pathName);

const CRUMB_LABELS: Record<string, string> = {
  [coverageUrl()]: "Coverage",
  [coverageWhatYouPayUrl()]: "What you pay",
};

export type Crumb = { href: string; label: string };

export const breadcrumbTrail = (pathName: string): Crumb[] => {
  const trail: Crumb[] = [];
  for (let route = pathName; route.startsWith(`${coverageUrl()}/`); ) {
    route = parentRoute(route);
    const label = CRUMB_LABELS[route];
    if (label) {
      trail.unshift({ href: route, label });
    }
  }
  return trail;
};

// Native opens these as sheets: an X to leave, a chevron below the first screen, a taller bar.
const SHEET_SECTION =
  /^\/(settings|people|hipaa-release|hipaa-release-preview|bill-dispute)(\/|$)/;

export const isSheetSection = (pathName: string) =>
  SHEET_SECTION.test(pathName);

/** Once in `_app`: every page counts toward the X's steps, including flows outside `HomeLayout`. */
export const useTrackSectionEntries = () => {
  const { pathname, asPath } = useNextRouter();
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs per history entry; `asPath` changes where `pathname` doesn't
  useEffect(
    () => rememberSectionEntry(isSheetSection(pathname)),
    [asPath, pathname],
  );
};
